import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { MapPin, Building2, Tags, Plus, Settings2, Trash2, UserX, Users as UsersIcon, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const LOCATION_TYPES = [
  { v: "lab", l: "Laboratory" },
  { v: "center", l: "Collection Center" },
  { v: "distribution", l: "B2B Partner" },
  { v: "field", l: "Field Staff (Sample Collection)" },
];

function LocationsMaster() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: locations = [] } = useQuery({ queryKey: ["locations"], queryFn: () => api.get("/locations").then((r) => r.data) });
  const blank = { name: "", type: "lab", code: "" };
  const [form, setForm] = useState(blank);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const create = async () => {
    if (!form.name || !form.code) { toast.error("Name and code are required"); return; }
    try {
      await api.post("/locations", form);
      toast.success("Location added");
      setForm(blank);
      qc.invalidateQueries({ queryKey: ["locations"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (l) => {
    try {
      await api.put(`/locations/${l._id}`, { active: !l.active });
      qc.invalidateQueries({ queryKey: ["locations"] });
      toast.success(l.active ? "Location deactivated" : "Location reactivated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/locations/${deleteTarget._id}`);
      toast.success("Location permanently deleted");
      qc.invalidateQueries({ queryKey: ["locations"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setDeleteTarget(null);
  };

  return (
    <div className="space-y-5">
      <Card className="p-6 bg-white border-slate-200">
        <div className="flex items-center gap-2 mb-4 text-navy">
          <Plus size={18} /> <span className="font-serif text-lg tracking-tight">Add Location</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="sm:col-span-2">
            <Label>Name</Label>
            <Input data-testid="location-name" placeholder="e.g. Jaipur Main Lab" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="location-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{LOCATION_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Code</Label>
            <Input data-testid="location-code" placeholder="e.g. LAB-JPR" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          </div>
        </div>
        <Button onClick={create} data-testid="location-submit" className="mt-4 text-white" style={{ background: "#042C53" }}>
          <Plus size={16} className="mr-2" /> Add Location
        </Button>
      </Card>

      <Card className="bg-white border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Code</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Staffing</th>
                <th className="px-4 py-3 font-semibold text-right">Active</th>
                <th className="px-4 py-3 font-semibold text-right">Delete</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((l, idx) => (
                <tr key={l._id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`location-row-${idx}`}>
                  <td className="px-4 py-3 font-medium text-navy">{l.name}</td>
                  <td className="px-4 py-3 text-slate-600">{LOCATION_TYPES.find((t) => t.v === l.type)?.l || l.type}</td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{l.code}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: l.active ? "#10B981" : "#94A3B8" }}>
                      {l.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {l.unstaffed ? (
                      <button
                        onClick={() => navigate(`/users?location=${l._id}`)}
                        data-testid={`location-unstaffed-${idx}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                        title="No active user assigned — click to assign one"
                      >
                        <UserX size={12} /> Unstaffed
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                        <UsersIcon size={12} /> {l.active_user_count} active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Switch checked={l.active} onCheckedChange={() => toggleActive(l)} data-testid={`location-toggle-${idx}`} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost" size="icon" onClick={() => setDeleteTarget(l)}
                      data-testid={`location-delete-${idx}`}
                      className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
              {locations.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No locations yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the location entirely — it cannot be undone. Only allowed when
              nothing references it (no stock, transactions, requisitions, or assigned users).
              If it's been used, delete will be blocked and you should deactivate it instead,
              which keeps past records intact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="location-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete} data-testid="location-delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DepartmentsMaster() {
  const qc = useQueryClient();
  const { data: departments = [] } = useQuery({ queryKey: ["departments"], queryFn: () => api.get("/departments").then((r) => r.data) });
  const [name, setName] = useState("");

  const create = async () => {
    if (!name.trim()) { toast.error("Department name is required"); return; }
    try {
      await api.post("/departments", { name: name.trim() });
      toast.success("Department added");
      setName("");
      qc.invalidateQueries({ queryKey: ["departments"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (d) => {
    try {
      await api.put(`/departments/${d._id}`, { active: !d.active });
      qc.invalidateQueries({ queryKey: ["departments"] });
      toast.success(d.active ? "Department deactivated" : "Department reactivated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <Card className="p-6 bg-white border-slate-200 max-w-2xl">
        <div className="flex items-center gap-2 mb-4 text-navy">
          <Plus size={18} /> <span className="font-serif text-lg tracking-tight">Add Department</span>
        </div>
        <div className="flex gap-3">
          <Input data-testid="department-name" placeholder="e.g. Hematology" value={name} onChange={(e) => setName(e.target.value)} />
          <Button onClick={create} data-testid="department-submit" className="text-white shrink-0" style={{ background: "#042C53" }}>
            <Plus size={16} className="mr-2" /> Add
          </Button>
        </div>
      </Card>

      <Card className="bg-white border-slate-200 overflow-hidden max-w-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Active</th>
              </tr>
            </thead>
            <tbody>
              {departments.map((d, idx) => (
                <tr key={d._id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`department-row-${idx}`}>
                  <td className="px-4 py-3 font-medium text-navy">{d.name}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: d.active ? "#10B981" : "#94A3B8" }}>
                      {d.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Switch checked={d.active} onCheckedChange={() => toggleActive(d)} data-testid={`department-toggle-${idx}`} />
                  </td>
                </tr>
              ))}
              {departments.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-400">No departments yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

const FIELD_GROUPS = [
  { v: "generic", l: "Standard (no extra fields)" },
  { v: "reagent", l: "Reagent / Control / Calibrator (batch-based)" },
  { v: "equipment_calibrated", l: "Lab Equipment (with calibration tracking)" },
  { v: "equipment_asset", l: "Office / IT Asset — e.g. Computer, Printer (no calibration)" },
];
const FIELD_GROUP_LABEL = Object.fromEntries(FIELD_GROUPS.map((g) => [g.v, g.l]));

function CategoriesMaster() {
  const qc = useQueryClient();
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: () => api.get("/categories").then((r) => r.data) });
  const [name, setName] = useState("");
  const [fieldGroup, setFieldGroup] = useState("generic");
  const [editTarget, setEditTarget] = useState(null);
  const [editFieldGroup, setEditFieldGroup] = useState("generic");

  const create = async () => {
    if (!name.trim()) { toast.error("Category name is required"); return; }
    try {
      await api.post("/categories", { name: name.trim(), field_group: fieldGroup });
      toast.success("Category added");
      setName("");
      setFieldGroup("generic");
      qc.invalidateQueries({ queryKey: ["categories"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/categories/${c._id}`, { active: !c.active });
      qc.invalidateQueries({ queryKey: ["categories"] });
      toast.success(c.active ? "Category deactivated" : "Category reactivated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveFieldGroup = async () => {
    if (!editTarget) return;
    try {
      await api.put(`/categories/${editTarget._id}`, { field_group: editFieldGroup });
      toast.success("Specification type updated");
      setEditTarget(null);
      qc.invalidateQueries({ queryKey: ["categories"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete the category "${c.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/categories/${c._id}`);
      toast.success("Category deleted");
      qc.invalidateQueries({ queryKey: ["categories"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <Card className="p-6 bg-white border-slate-200 max-w-2xl">
        <div className="flex items-center gap-2 mb-4 text-navy">
          <Plus size={18} /> <span className="font-serif text-lg tracking-tight">Add Category</span>
        </div>
        <div className="space-y-3">
          <Input data-testid="category-name" placeholder="e.g. Reagent, IT Equipment" value={name} onChange={(e) => setName(e.target.value)} />
          <div>
            <Label className="text-xs text-slate-500">Specification Type</Label>
            <p className="text-xs text-slate-400 mb-1">Decides which extra fields the Item Master form shows for this category.</p>
            <Select value={fieldGroup} onValueChange={setFieldGroup}>
              <SelectTrigger data-testid="category-field-group"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {FIELD_GROUPS.map((g) => <SelectItem key={g.v} value={g.v}>{g.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={create} data-testid="category-submit" className="text-white w-full" style={{ background: "#042C53" }}>
            <Plus size={16} className="mr-2" /> Add Category
          </Button>
        </div>
      </Card>

      <Card className="bg-white border-slate-200 overflow-hidden max-w-3xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Specification Type</th>
                <th className="px-4 py-3 font-semibold">Items Using It</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Active</th>
                <th className="px-4 py-3 font-semibold text-right">Edit</th>
                <th className="px-4 py-3 font-semibold text-right">Delete</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((c, idx) => (
                <tr key={c._id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`category-row-${idx}`}>
                  <td className="px-4 py-3 font-medium text-navy">{c.name}</td>
                  <td className="px-4 py-3 text-slate-600">{FIELD_GROUP_LABEL[c.field_group] || FIELD_GROUP_LABEL.generic}</td>
                  <td className="px-4 py-3 text-slate-600">{c.item_count}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: c.active ? "#10B981" : "#94A3B8" }}>
                      {c.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Switch checked={c.active} onCheckedChange={() => toggleActive(c)} data-testid={`category-toggle-${idx}`} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost" size="icon" data-testid={`category-edit-${idx}`}
                      onClick={() => { setEditTarget(c); setEditFieldGroup(c.field_group || "generic"); }}
                      className="text-slate-400 hover:text-teal hover:bg-slate-100"
                    >
                      <Pencil size={16} />
                    </Button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`category-delete-${idx}`}
                      disabled={c.item_count > 0}
                      title={c.item_count > 0 ? "Deactivate instead — items still use this category" : "Delete category"}
                      onClick={() => remove(c)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
              {categories.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No categories yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-navy text-xl">Edit "{editTarget?.name}"</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs text-slate-500">Specification Type</Label>
            <p className="text-xs text-slate-400 mb-1">Existing items in this category keep their saved data either way — this only changes which fields the Add/Edit Item form shows going forward.</p>
            <Select value={editFieldGroup} onValueChange={setEditFieldGroup}>
              <SelectTrigger data-testid="category-edit-field-group"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {FIELD_GROUPS.map((g) => <SelectItem key={g.v} value={g.v}>{g.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button onClick={saveFieldGroup} data-testid="category-edit-save" style={{ background: "#042C53" }} className="text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Masters() {
  return (
    <div className="space-y-5" data-testid="masters-page">
      <div>
        <h2 className="font-serif text-3xl tracking-tight text-navy">Masters</h2>
        <p className="text-slate-500 text-sm mt-1">
          <Settings2 size={14} className="inline mr-1" style={{ color: "#0a8f8f" }} />
          Manage locations, departments and item categories used across the system — update these any time.
        </p>
      </div>

      <Tabs defaultValue="locations">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="locations" data-testid="tab-locations"><MapPin size={14} className="mr-1.5" /> Locations</TabsTrigger>
          <TabsTrigger value="departments" data-testid="tab-departments"><Building2 size={14} className="mr-1.5" /> Departments</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories"><Tags size={14} className="mr-1.5" /> Categories</TabsTrigger>
        </TabsList>
        <TabsContent value="locations"><LocationsMaster /></TabsContent>
        <TabsContent value="departments"><DepartmentsMaster /></TabsContent>
        <TabsContent value="categories"><CategoriesMaster /></TabsContent>
      </Tabs>
    </div>
  );
}
